import PropTypes from 'prop-types';
import { NewBookingDeliveryPropType } from 'common/prop-types';
import React, { useCallback, useMemo } from 'react';
import { connect } from 'react-redux';
import I18n from 'i18n-js';
import { deliveriesFromPropsSelector } from 'selectors/new-booking-selectors';
import _get from 'lodash/get';
import _cloneDeep from 'lodash/cloneDeep';
import { mergeDisplayError } from 'helper/anytime-booking';
import NewBookingDuck from 'ducks/new-booking-duck';
import cx from 'classnames';
import circleCross from 'public/icons/circle-cross.svg';
import circlePlus from 'public/icons/circle-plus.svg';
import WithTooltip from 'components/v2/with-tooltip-hoc';
import _isEmpty from 'lodash/isEmpty';
import _every from 'lodash/every';
import { codAdditionalServiceID } from 'constants/anytime-booking-constants';
import NewBulkUploadDuck from 'ducks/new-bulk-upload-duck';
import styles from './styles.less';
import OverlayBody from '../overlay-body';

const BulkAddOnPanel = ({ dispatch, onClose, deliveryIDs, deliveries }) => {
  const onConfirm = useCallback(
    () => {
      onClose();
    },
    [onClose],
  );

  const onBack = useCallback(
    () => {
      onClose();
    },
    [onClose],
  );

  const selectedAddOn = useMemo(
    () => {
      const addOnMapCount = {};
      const addOns = deliveries.map(delivery =>
        _get(delivery, 'detail.configuration.additionalServices'),
      );
      addOns.flat().forEach(addOn => {
        addOnMapCount[addOn.id] = _get(addOnMapCount, addOn.id, 0) + 1;
      });
      if (!_isEmpty(addOnMapCount)) {
        // All delivery should have same addOns
        const allHaveSameAddOns = _every(
          Object.keys(addOnMapCount),
          key => addOnMapCount[key] === deliveries.length,
        );
        if (allHaveSameAddOns) {
          // Return add ons list
          return addOns[0];
        }
        return [];
      }
      return [];
    },
    [deliveries],
  );

  const addOnToRender = useMemo(
    () => {
      const allAddOnMap = {};
      const addOnAvailableCount = {};

      deliveries.forEach(delivery => {
        const ast = _get(delivery, 'config.additionalServices', []);
        ast
          .filter(addOn => addOn.id !== codAdditionalServiceID)
          .forEach(addOn => {
            const errorMessage = _get(addOn, 'errorMessage');
            if (!allAddOnMap[addOn.id]) {
              allAddOnMap[addOn.id] = _cloneDeep(addOn);
            }
            if (!errorMessage) {
              addOnAvailableCount[addOn.id] =
                _get(addOnAvailableCount, addOn.id, 0) + 1;
            } else {
              allAddOnMap[addOn.id].errorMessage = errorMessage;
            }
          });
      });

      const allAddOns = Object.values(allAddOnMap);
      allAddOns.forEach(addOn => {
        addOn.key = addOn.id;
        addOn.description = _get(addOn, 'metadata.data.description', '');
        addOn.disabled =
          !!addOn.errorMessage ||
          addOnAvailableCount[addOn.id] < deliveries.length;
        if (addOn.errorMessage) {
          const { reason, hint } = mergeDisplayError(addOn.errorMessage);
          addOn.disabledReason = reason;
          addOn.disabledHint = hint;
        }
      });
      return allAddOns.sort((a, b) => a.id.localeCompare(b.id));
    },
    [deliveries, selectedAddOn],
  );

  const onSelectAddOn = useCallback(
    (addOn, disabled) => () => {
      if (!disabled) {
        const isRemove = selectedAddOn.find(sa => sa.id === addOn.id);
        let addOnList;
        if (isRemove) {
          addOnList = selectedAddOn.filter(sa => sa.id !== addOn.id);
        } else {
          addOnList = [
            ...selectedAddOn,
            {
              id: addOn.id,
              name: addOn.name,
              data: {
                enabled: true,
              },
            },
          ];
        }
        dispatch(
          NewBookingDuck.creators.bulkUpdateServiceConfig(deliveryIDs, {
            additionalServices: addOnList,
          }),
        );
        dispatch(NewBulkUploadDuck.creators.trySetOfferConfig(null));
      }
    },
    [dispatch, deliveryIDs, selectedAddOn, deliveries],
  );

  return (
    <OverlayBody
      showConfirm
      disabledConfirm={selectedAddOn === undefined}
      onConfirmClick={onConfirm}
      showHeader
      showTitle
      title={I18n.t('bulkUploadRevamp.overlay.addOn.title')}
      showClose
      onClose={onBack}
    >
      <div className={styles.addOn}>
        {addOnToRender.map(addOn => {
          const selected = !!selectedAddOn.find(sa => sa.id === addOn.id);
          const disabled = !!addOn.disabledHint;

          const renderAddOn = (
            <a
              key={addOn.key}
              className={cx(styles.option, {
                [styles.selected]: selected && !disabled,
                [styles.notselected]: !selected && !disabled,
                [styles.disabled]: disabled,
              })}
              onClick={onSelectAddOn(addOn, disabled)}
            >
              <div className={styles.intro}>
                <span
                  className={cx(styles.title, {
                    [styles.selected]: selected && !disabled,
                    [styles.notselected]: !selected && !disabled,
                    [styles.disabled]: disabled,
                  })}
                >
                  {addOn.name}
                </span>
                <div className={styles.description}>{addOn.description}</div>
              </div>

              <img
                className={cx(styles.icon)}
                src={selected ? circleCross : circlePlus}
                alt=""
              />
            </a>
          );
          if (!disabled) {
            return renderAddOn;
          }

          return (
            <WithTooltip
              key={_get(addOn, 'key')}
              hasTooltip={!!addOn.disabledHint}
              title={addOn.disabledHint}
              placement={'bottom'}
            >
              {renderAddOn}
            </WithTooltip>
          );
        })}
      </div>
    </OverlayBody>
  );
};

BulkAddOnPanel.propTypes = {
  dispatch: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  deliveryIDs: PropTypes.arrayOf(PropTypes.string).isRequired,
  deliveries: PropTypes.arrayOf(NewBookingDeliveryPropType).isRequired,
};

export default connect((state, props) => ({
  deliveries: deliveriesFromPropsSelector(state, props),
}))(BulkAddOnPanel);
